import { createTheme } from '@mui/material/styles';
import colors from 'assets/scss/_themes-vars.module.scss';
import componentStyleOverrides from './compStyleOverride';
import {
  bgMainContentColor,
  blackColor,
  dark800,
  dark900,
  darkColor,
  darkDark,
  darkLight,
  darkMain,
  darkTextSecondary,
  grey100,
  grey200,
  grey500,
  grey700,
  grey900,
  greyOutlineColor,
  orangeDark,
  orangeLight,
  orangeMain,
  whiteColor,
  yellowMain,
  yellowMainDark,
  yellowMainLight,
} from './palette';

declare module '@mui/material/styles' {
  interface OptionChooseColor {
    main: string;
    light?: string;
    dark?: string;
    900?: string;
    800?: string;
    700?: string;
    600?: string;
    500?: string;
    400?: string;
    300?: string;
    200?: string;
    100?: string;
  }
  interface Palette {
    yellowMainColor: OptionChooseColor;
    heading: OptionChooseColor;
    dark: OptionChooseColor;
    yellowMain: OptionChooseColor;
    blackColor: OptionChooseColor;
    greyOutlineColor: OptionChooseColor;
    darkColor: OptionChooseColor;
    whiteColor: OptionChooseColor;
    orangeColor: OptionChooseColor;
    orange: OptionChooseColor;
  }
  interface PaletteOptions {
    yellowMainColor: OptionChooseColor;
    heading: OptionChooseColor;
    dark: OptionChooseColor;
    yellowMain: OptionChooseColor;
    blackColor: OptionChooseColor;
    greyOutlineColor: OptionChooseColor;
    darkColor: OptionChooseColor;
    whiteColor: OptionChooseColor;
    orangeColor: OptionChooseColor;
    orange: OptionChooseColor;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    YellowMainColor: true;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    customOther?: {
      mainContent?: any;
      customInput?: any;
      subMenuCaption: any;
      commonAvatar: any;
      smallAvatar: any;
      mediumAvatar: any;
      largeAvatar: any;
    };
  }
  interface ThemeOptions {
    customOther?: {
      mainContent?: any;
      customInput?: any;
      subMenuCaption: any;
      commonAvatar: any;
      smallAvatar: any;
      mediumAvatar: any;
      largeAvatar: any;
    };
  }
}
export const theme = () => {
  const color = colors;

  const themeOption = {
    colors: color,
    heading: grey900,
    headerTable: grey100,
    paper: blackColor,
    backgroundDefault: whiteColor,
    background: grey100,
    darkTextPrimary: grey700,
    darkTextSecondary: grey500,
    textDark: grey900,
    menuSelected: yellowMainDark,
    menuSelectedBack: yellowMainLight,
    divider: grey200,
    yellowMain: yellowMainDark,
  };

  const themes = createTheme({
    palette: {
      heading: {
        main: grey900,
      },
      dark: {
        light: darkLight,
        main: darkMain,
        dark: darkDark,
        800: dark800,
        900: dark900,
      },
      yellowMainColor: {
        main: yellowMain,
        light: yellowMainLight,
        dark: yellowMainDark,
      },
      yellowMain: {
        main: yellowMain,
        light: yellowMainLight,
        dark: yellowMainDark,
      },
      blackColor: {
        main: blackColor,
      },
      whiteColor: {
        main: whiteColor,
      },
      greyOutlineColor: {
        main: greyOutlineColor,
      },
      darkColor: {
        main: darkColor,
      },
      orangeColor: {
        main: orangeMain,
        light: orangeLight,
        dark: orangeDark,
      },
      orange: {
        main: orangeMain,
        light: orangeLight,
        dark: orangeDark,
      },
    },
    mixins: {
      toolbar: {
        minHeight: '48px',
        padding: '16px',
        '@media (min-width: 600px)': {
          minHeight: '48px',
        },
      },
    },
    typography: {
      fontFamily: 'google-sans medium, sans-serif',
      h6: {
        fontWeight: 500,
        color: '',
        fontSize: '0.75rem',
      },
      h5: {
        fontSize: '0.875rem',
        color: blackColor,
        fontWeight: 500,
      },
      h4: {
        fontSize: '1rem',
        color: blackColor,
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.25rem',
        color: blackColor,
        fontWeight: 600,
      },
      h2: {
        fontSize: '1.5rem',
        color: blackColor,
        fontWeight: 700,
      },
      h1: {
        fontSize: '2.125rem',
        color: blackColor,
        fontWeight: 700,
      },
      subtitle1: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: blackColor,
      },
      subtitle2: {
        fontSize: '0.75rem',
        fontWeight: 400,
        color: blackColor,
      },
      caption: {
        fontSize: '0.75rem',
        color: blackColor,
        fontWeight: 400,
      },
      body1: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.334em',
      },
      body2: {
        letterSpacing: '0em',
        fontWeight: 400,
        lineHeight: '1.5em',
        color: blackColor,
      },
      button: {
        textTransform: 'capitalize',
      },
    },
    components: componentStyleOverrides(themeOption),
    customOther: {
      mainContent: {
        backgroundColor: bgMainContentColor,
        width: '100%',
        minHeight: 'calc(100vh - 88px)',
        flexGrow: 1,
        padding: '20px',
        // marginTop: '88px',
        marginTop: '40px',
        marginRight: '20px',
        borderRadius: '4px',
      },
      customInput: {
        marginTop: 1,
        marginBottom: 1,
        '& > label': {
          top: 23,
          left: 0,
          color: grey500,
          '&[data-shrink="false"]': {
            top: 5,
          },
        },
        '& > div > input': {
          padding: '30.5px 14px 11.5px !important',
        },
        '& legend': {
          display: 'none',
        },
        '& fieldset': {
          top: 0,
        },
      },
      subMenuCaption: {
        fontSize: '0.6875rem',
        fontWeight: 500,
        color: darkTextSecondary,
        textTransform: 'capitalize',
      },
      commonAvatar: {
        cursor: 'pointer',
        borderRadius: '8px',
      },
      smallAvatar: {
        width: '22px',
        height: '22px',
        fontSize: '1rem',
      },
      mediumAvatar: {
        width: '34px',
        height: '34px',
        fontSize: '1.2rem',
      },
      largeAvatar: {
        width: '44px',
        height: '44px',
        fontSize: '1.5rem',
      },
    },
  });
  return themes;
};

export default theme;
